import { useAuth } from '@teachino/shared-auth';
import { $t } from '@teachino/shared-i18n';
import { useGlobalDependencyProvider } from '@teachino/shared-util';
import { computed, type ComputedRef, ref } from 'vue';
import * as z from 'zod';
import { Upload } from './upload';

const uploadSchema = z.object({
  key: z.string().min(1),
  originalName: z.string().min(1),
  url: z.string().min(1),
});

export type UploadResponse = z.infer<typeof uploadSchema>;

export type UploadStatus =
  | { status: 'waiting' }
  | {
      status: 'uploading';
      progressPercent: number;
      file: File;
    }
  | {
      status: 'done';
      upload: Upload;
    }
  | { status: 'cancelled'; file: File }
  | { status: 'error'; error: string; file: File };

export type FileHandlerConfig = {
  useDashboardPod?: boolean;
  /**
   * Full upload URL override. When provided, it is used verbatim instead of the
   * default authenticated `/upload/file` endpoint — e.g. a public,
   * session-scoped interactive-assignment upload endpoint.
   */
  uploadUrl?: string;
  /**
   * Whether to attach the teacher JWT `Authorization` header (and retry on 401).
   * Set to `false` for public/anonymous uploads. Defaults to `true`.
   */
  useAuthHeader?: boolean;
};

export class FileHandler {
  private di = useGlobalDependencyProvider();
  private config = this.di.inject('config');
  private auth = useAuth();
  private useDashboardPod: boolean;
  private uploadUrl: string | null;
  private useAuthHeader: boolean;

  constructor({
    useDashboardPod = false,
    uploadUrl,
    useAuthHeader = true,
  }: FileHandlerConfig = {}) {
    this.useDashboardPod = useDashboardPod;
    this.uploadUrl = uploadUrl ?? null;
    this.useAuthHeader = useAuthHeader;
  }

  private _uploadStatus = ref<UploadStatus>({ status: 'waiting' });
  private activeXhr: XMLHttpRequest | null = null;
  private isUploadCancelled = false;

  static fromUpload(upload: Upload): FileHandler {
    const handler = new FileHandler();
    handler.setDoneStatus(upload);
    return handler;
  }

  uploadStatus: ComputedRef<UploadStatus> = computed<UploadStatus>(
    () => this._uploadStatus.value,
  );

  uploadFile(args: { file: File }): Promise<Upload> {
    this.reset();
    const url =
      this.uploadUrl ??
      `${this.useDashboardPod ? this.config.exportBaseUrl() : this.config.baseUrl()}/upload/file`;

    const { file } = args;
    const formData = new FormData();
    const fixedName = file.name.normalize('NFC');
    formData.append('file', file, fixedName);

    const donePromise = new Promise<Upload>((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        this.activeXhr = xhr;
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progressPercent = Math.round(
              (event.loaded / event.total) * 100,
            );
            this.setUploadingStatus({ file, progressPercent });
          }
        });

        xhr.addEventListener('load', () => {
          if (this.isUploadCancelled) {
            this.setCancelledStatus(file);
            reject($t('generic_cancel'));
            return;
          }

          const httpStatusUnauthorized = 401;
          if (this.useAuthHeader && xhr.status === httpStatusUnauthorized) {
            this.auth
              .refreshAuth()
              .then(() => this.uploadFile(args))
              .then(resolve)
              .catch(() => {
                this.setErrorStatusAndReject({ file, reject });
              });
            return;
          }

          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          if (xhr.status >= 200 && xhr.status < 300) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const responseJson = JSON.parse(xhr.responseText);
            const response = uploadSchema.parse(responseJson);
            const upload = new Upload(response);
            this.setDoneStatus(upload);
            resolve(upload);
          } else {
            this.setErrorStatusAndReject({ file, reject });
          }
        });
        xhr.addEventListener('error', () => {
          if (this.isUploadCancelled) {
            this.setCancelledStatus(file);
            reject($t('generic_cancel'));
            return;
          }
          this.setErrorStatusAndReject({ file, reject });
        });
        xhr.addEventListener('abort', () => {
          this.setCancelledStatus(file);
          reject($t('generic_cancel'));
        });

        xhr.open('POST', url);
        if (this.useAuthHeader) {
          const jwt = this.auth.getJwt();
          if (!jwt) {
            this.setErrorStatusAndReject({ file, reject });
            return;
          }
          xhr.setRequestHeader('Authorization', jwt);
        }
        xhr.send(formData);
      } catch {
        this.setErrorStatusAndReject({ file, reject });
      }
    });
    return donePromise;
  }

  cancelUpload() {
    this.isUploadCancelled = true;
    this.activeXhr?.abort();
    this.activeXhr = null;
  }

  delete() {
    this.reset();
  }

  private reset() {
    this.activeXhr = null;
    this.isUploadCancelled = false;
    this.setWaitingStatus();
  }

  private setWaitingStatus() {
    this._uploadStatus.value = { status: 'waiting' };
  }

  private setErrorStatusAndReject(args: {
    file: File;
    reject: (reason: string) => void;
  }) {
    const { file, reject } = args;
    const error = $t('generic_error');
    reject(error);
    this.activeXhr = null;
    this._uploadStatus.value = { status: 'error', error, file };
  }

  private setCancelledStatus(file: File) {
    this.activeXhr = null;
    this._uploadStatus.value = { status: 'cancelled', file };
  }

  private setDoneStatus(upload: Upload) {
    this.activeXhr = null;
    this._uploadStatus.value = {
      status: 'done',
      upload,
    };
  }

  private setUploadingStatus(args: { file: File; progressPercent: number }) {
    const { file, progressPercent } = args;
    this._uploadStatus.value = {
      status: 'uploading',
      file,
      progressPercent,
    };
  }
}
